import * as React from 'react';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {ErrorMessage} from 'shells/form/error-message';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {FooterBar} from 'shells/modal/footer-bar';
import {Modal} from 'shells/modal/modal';
import {MarketingHeader} from 'shells/typography/marketing-header';
import {MarketingTitle} from 'shells/typography/marketing-title';
import {ModalBody} from 'shells/typography/modal-body';
import {ModalTitle} from 'shells/typography/modal-title';

import type {EditionFragment as DripEdition, EditionUpdateInput} from 'nutshell-graphql-types';

import {
    SubjectActionBox,
    UnsubscribeActionBox,
    StreetAddressActionBox,
} from '../../editions/edition-action-boxes';

import styles from './publish-changes-modal.module.css';

type ModalState = 'prepare-to-publish' | 'ready-to-publish';

type Props = {
    edition: DripEdition;
    unsavedDetails: EditionUpdateInput | null | undefined;
    isLoading: boolean;
    isSaving: boolean;
    streetAddress: string | null | undefined;
    canAccessSetup: boolean;
    onModalClose: () => void;
    onPublishChanges: () => void;
};

export function PublishChangesModal(props: Props) {
    const [modalState, setModalState] = React.useState<ModalState>('prepare-to-publish');

    // Compare unsavedDetails to actual edition to get current values
    let currentSubject = props.edition.emailTemplate && props.edition.emailTemplate.subject;
    let currentPreviewText = props.edition.emailTemplate && props.edition.emailTemplate.previewText;
    if (props.unsavedDetails) {
        if (props.unsavedDetails.subject !== currentSubject) {
            // @ts-expect-error
            currentSubject = props.unsavedDetails.subject;
        }

        if (props.unsavedDetails.previewText !== currentPreviewText) {
            // @ts-expect-error
            currentPreviewText = props.unsavedDetails.previewText;
        }
    }

    const isMessageComplete =
        currentSubject &&
        props.edition.emailTemplate &&
        props.edition.hasUnsubscribeLink &&
        props.edition.hasStreetAddress &&
        props.streetAddress;

    const prepareToPublishPage = (
        <>
            <MarketingHeader>Prepare to publish changes</MarketingHeader>
            <ModalBody>
                {isMessageComplete ? (
                    'This updated message has all required items'
                ) : (
                    <ErrorMessage>This updated message is missing required items</ErrorMessage>
                )}
            </ModalBody>
            <div className={styles['action-box-container']}>
                <SubjectActionBox
                    edition={{
                        ...props.edition,
                        emailTemplate: {
                            ...props.edition.emailTemplate,
                            // @ts-expect-error
                            subject: currentSubject || undefined,
                        },
                    }}
                />
                <UnsubscribeActionBox edition={props.edition} />
                <StreetAddressActionBox
                    edition={props.edition}
                    streetAddress={props.streetAddress}
                    canAccessSetup={props.canAccessSetup}
                />
            </div>
            <FooterBar
                left={
                    <Button variant='text' size='big' onClick={props.onModalClose}>
                        Cancel
                    </Button>
                }
                right={
                    <Button
                        variant='primary'
                        size='big'
                        onClick={() => {
                            setModalState('ready-to-publish');
                        }}
                        disabled={!isMessageComplete}
                    >
                        Review
                    </Button>
                }
            />
        </>
    );

    const readyToPublishPage = (
        <>
            <MarketingHeader>Ready to publish changes</MarketingHeader>
            <MarketingTitle>
                You’re about to publish changes to an active drip sequence
            </MarketingTitle>
            <ModalBody>
                This action cannot be undone, so double check that everything looks correct before
                proceeding.
            </ModalBody>
            {/* TODO: Styles may need to be updated / more consistent with <PreviewView /> */}
            <div className={styles['message-preview-container']}>
                <div>
                    <div className={styles['preview-item']}>
                        <ModalTitle>Message</ModalTitle>
                        <ModalBody>{props.edition.name}</ModalBody>
                    </div>
                    {props.edition.emailTemplate ? (
                        <div className={styles['thumbnail-container']}>
                            <img
                                // @ts-expect-error
                                src={props.edition.emailTemplate.thumbnailUrl}
                                alt={props.edition.name}
                            />
                        </div>
                    ) : undefined}
                </div>
                <div>
                    <div className={styles['preview-item']}>
                        <ModalTitle>Subject line</ModalTitle>
                        <ModalBody>
                            {currentSubject ? (
                                currentSubject
                            ) : (
                                // This should not ever happen ... but
                                <i>None</i>
                            )}
                        </ModalBody>
                    </div>
                    <div className={styles['preview-item']}>
                        <ModalTitle>Preview text</ModalTitle>
                        <ModalBody>
                            {currentPreviewText ? currentPreviewText : <i>None</i>}
                        </ModalBody>
                    </div>
                </div>
            </div>
            <FooterBar
                left={
                    <Button
                        variant='text'
                        size='big'
                        onClick={() => {
                            setModalState('prepare-to-publish');
                        }}
                    >
                        Back
                    </Button>
                }
                right={
                    <SaveButton
                        isSaving={props.isSaving}
                        // @ts-expect-error
                        onClick={props.onPublishChanges}
                        savingText='Publishing...'
                        disabled={!isMessageComplete}
                        buttonText='Publish changes'
                    />
                }
            />
        </>
    );

    return (
        <Modal isOpen={true} maxHeight={600} onClose={props.onModalClose}>
            {props.isLoading ? (
                <LoadingPage />
            ) : (
                <div className={styles['publish-changes-modal']}>
                    {modalState === 'prepare-to-publish'
                        ? prepareToPublishPage
                        : readyToPublishPage}
                </div>
            )}
        </Modal>
    );
}
